import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication } from '@azure/msal-browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { App } from './app'
import { AuthProvider } from './auth/auth-provider'
import { msalConfig } from './auth/msal-config'
import { useMockAuth, useMockBff } from './dev/flags'
import './index.css'

// QueryClient is instantiated once at module scope. Defaults are tightened
// to match the audit-emit-then-paint posture in ADR-013 - no background
// refetch on focus while a reveal is in flight.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Missing #root element in index.html')
}

// Bootstrap is wrapped in an async IIFE so we can `await` the dev-mode
// MSW worker before rendering. The real MSAL instance is constructed only
// when `VITE_USE_MOCK_AUTH` is NOT set, so the prod bundle does not pay
// the cost of building one that gets thrown away.
//
// Tree-shaking note: both dev modules (`./dev/mock-auth-provider`,
// `./dev/mock-bff`) are reached only via dynamic `import()` calls behind
// `useMockAuth` / `useMockBff` checks. Vite's `define` plugin folds
// `import.meta.env.VITE_USE_MOCK_*` into string literals at build time,
// so when the env var is absent or != "true" the dynamic branches are
// dead code and the dev chunk is not emitted. Verified via `npm run build`
// (no `mock-auth-provider` / `mock-bff` chunks under `dist/assets/`).
void (async () => {
  if (useMockBff) {
    const { startMockBff } = await import('./dev/mock-bff')
    await startMockBff()
  }

  let authBoundary: (children: ReactNode) => JSX.Element
  if (useMockAuth) {
    const { MockAuthProvider } = await import('./dev/mock-auth-provider')
    authBoundary = (children) => <MockAuthProvider>{children}</MockAuthProvider>
  } else {
    // Real MSAL: instantiate here so the construction cost is paid only
    // when we are actually wiring up the real IdP.
    const msalInstance = new PublicClientApplication(msalConfig)
    authBoundary = (children) => <AuthProvider instance={msalInstance}>{children}</AuthProvider>
  }

  createRoot(rootElement).render(
    <StrictMode>
      {authBoundary(
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>,
      )}
    </StrictMode>,
  )
})()
